import axios from 'axios';
import parseLinkHeader from 'parse-link-header';

import { addAlert } from 'components/shared/Alert';
import { refreshToken, replayFailedApiCalls, storeFailedApiCall } from 'redux/auth/actions';
import { publicRoutes } from 'router';
import { getJwt, logout, setJwt } from 'utils/jwt';

let isRefreshingToken = false;

async function refreshTokenAndRetryRequest(error) {
    async function waitOneSec() {
        return new Promise((resolve) => setTimeout(resolve, 1000));
    }

    async function waitForRefresh() {
        if (isRefreshingToken) {
            await waitOneSec();
            await waitForRefresh();
        }
    }

    try {
        const { response: errorResponse } = error;
        const jwt = getJwt();

        if (jwt) {
            if (isRefreshingToken) {
                await waitForRefresh();
            } else {
                isRefreshingToken = true;
                const response = await axios({
                    data: { token: jwt },
                    method: 'post',
                    url: `${process.env.REACT_APP_API_URL}/auth/refresh`,
                });

                if (!response.data) {
                    isRefreshingToken = false;
                    throw error;
                }

                const { token } = response.data;
                setJwt(token);
                isRefreshingToken = false;
            }

            errorResponse.config.headers.Authorization = `Bearer ${getJwt()}`;
            return axios(errorResponse.config);
        }

        throw error;
    } catch (err) {
        const publicRoute = publicRoutes.find(({ path }) => {
            if (Array.isArray(path)) {
                return path.find((p) => window.location.href.includes(p));
            }

            return window.location.href.includes(path);
        });

        if (!publicRoute) {
            logout(true);
        }

        throw err;
    }
}

const setAuthorizationHeader = (config, jwt) => {
    let token = jwt;

    // If the user has an old token that was stored with the "Bearer " prefix, remove it
    if (token.includes('Bearer ')) {
        token = token.replace('Bearer ', '');
    }

    return {
        ...config,
        headers: { ...config.headers, Authorization: `Bearer ${token}` }
    };
};

function requestErrorInterceptor(error) {
    return Promise.reject(error);
}

function requestSuccessInterceptor(config) {
    const jwt = getJwt();

    if (jwt) {
        return setAuthorizationHeader(config, jwt);
    }

    return config;
}

function responseErrorInterceptor(error) {
    const { response } = error;
    // eslint-disable-next-line prefer-promise-reject-errors
    if (!response) return Promise.reject({ ...error, status: 504 });
    const { data, headers, request, status } = response;
    const { responseURL } = request;

    // if refresh failed
    if (responseURL.includes('/api/v1/auth/refresh')) {
        return logout(true);
    }

    const getMessage = (obj) => {
        const message = obj[Object.keys(obj)[0]];
        return Array.isArray(message) ? message[0] : message;
    };

    switch (status) {
        case 400: // bad request
            if (responseURL.includes('/subscriptions/card/verify')) {
                break;
            }

            addAlert({ message: getMessage(data) });
            break;

        case 401: // unauthorized
            try {
                refreshTokenAndRetryRequest(error);
            } catch (e) {
                return Promise.reject(e);
            }

            break;

        case 402: // no subscription
            if (window.location.pathname !== '/signup') {
                window.history.href = '/signup';
            }

            break;

        case 403: // forbidden
            if (responseURL.includes('/api/v1/subscriptions/retention')) {
                break;
            }

            addAlert({ message: getMessage(data) });
            break;

        case 404: // not found
            if (responseURL.includes('/api/v1/coupons/validate')) {
                break;
            }

            addAlert({ message: getMessage(data) });
            break;

        case 422: // invalid data from user
            addAlert({ message: getMessage(data) });
            break;

        case 429: // Rate limit
            addAlert({ message: `Too many requests. Retry in ${headers['retry-after']} seconds.` });
            break;

        case 503: // maintenance
            window.history.href = '/maintenance';
            break;

        default:
            if (data.message) {
                addAlert({ message: data.message });
                break;
            }
            addAlert({ message: 'There was an error with your request.' });
            break;
    }

    // If the error is due to other reasons, we just throw it back to axios
    return Promise.reject(error);
}

function responseSuccessInterceptor(response) {
    const headers = {
        link: '<>; rel="previous", <>; rel="next"',
        'x-pages-current-page': '1',
        'x-pages-last-page': '1',
        'x-pages-total-count': '1',
        ...response.headers,
    };
    const link = parseLinkHeader(headers.link);
    const res = { ...response };
    res.headers = headers;
    res.pagination = {
        currentPage: Number(res.headers['x-pages-current-page']),
        lastPage: Number(res.headers['x-pages-last-page']),
        nextPage: link.next.url,
        previousPage: link.previous.url,
        totalCount: Number(res.headers['x-pages-total-count'])
    };

    return res;
}

export const authHttp = axios.create({
    baseURL: process.env.REACT_APP_API_URL,
});

authHttp.interceptors.request.use(requestSuccessInterceptor, requestErrorInterceptor);

authHttp.interceptors.response.use(
    responseSuccessInterceptor,
    responseErrorInterceptor
);

export const http = axios.create({ baseURL: process.env.REACT_APP_API_URL });

export const httpConfig = {
    interceptors: {
        request: [{
            error: (options, error) => requestErrorInterceptor(error),
            success: (data, config) => requestSuccessInterceptor(config),
        }],
        response: [{
            error: ({ dispatch, getSourceAction }, error) => {
                if (error.response.status === 401) {
                    dispatch(storeFailedApiCall(getSourceAction(error.config)));
                    dispatch(refreshToken()).then(() => {
                        dispatch(replayFailedApiCalls());
                    });
                } else {
                    return responseErrorInterceptor(error);
                }

                return Promise.reject(error);
            },
            success: (options, response) => responseSuccessInterceptor(response),
        }],
    },
    returnRejectedPromiseOnError: true,
};

export default http;
